"use client";

import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { Check, LayoutGrid, Map as MapIcon } from "lucide-react";
import {
  buildTrailStations,
  getTrailCategoryPath,
  getTrailHref,
} from "@/lib/article-trail";
import { SubjectProgressTrail } from "@/components/article-shell/TrailMap";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * 공부 진행상황 — 이론/코딩 과목별 상세 뷰 전환 (JCG-2163).
 *
 * 한 카테고리(이론 or 코딩)의 과목들을 두 가지로 보여주고 상단 토글로 전환한다.
 * - **출석 뷰(기본)**: 전 과목의 학습 경로 페이지를 컴팩트 그리드(체크 칸)로 쌓아 한눈에.
 * - **지도 뷰**: 과목 선택 칩 + 선택한 과목의 오솔길(TrailMap 비주얼 재활용).
 *
 * 두 뷰 모두 **같은 출석 데이터**(`attendedSlugs`)와 **같은 페이지 집합**(학습 경로
 * = `buildTrailStations`)을 쓰고 표현만 다르게 한다. 출석 데이터는 부모(StudyProgressPage)가
 * 이미 받은 `/api/study-progress` 의 attendedPages 합집합이라 추가 fetch 가 없다.
 */

type SubjectAttendance = {
  subject: string;
  subjectDisplayName: string;
  attendedPages: Array<{ slug: string; attendedAt: string; reviewCount: number }>;
  attendedCount: number;
  totalPages: number;
};

/**
 * 과목 키 → 학습 경로(카테고리) 경로.
 *
 * 하드코딩 맵을 따로 두지 않고 `/{category}/{key}`(소문자)로 파생한 뒤, article-trail SSOT
 * (`getTrailCategoryPath` → subject-posts 의 `SubjectData.href`)로 실재하는 경로인지 검증한다.
 * (예: "DB" → "/theory/db", "network-os" → "/theory/network-os", "c-language" → "/coding/c-language").
 * 트레일이 없는 키는 null → 호출부에서 칩 비활성/placeholder 처리. 과목이 늘어도 SSOT 한 곳만 보면 된다.
 */
function categoryPathFor(
  category: "theory" | "coding",
  subjectKey: string,
): string | null {
  return getTrailCategoryPath(`/${category}/${subjectKey.toLowerCase()}`);
}

const VIEW_TABS = [
  { key: "attendance", label: "출석", icon: LayoutGrid },
  { key: "map", label: "지도", icon: MapIcon },
] as const;

type ViewKey = (typeof VIEW_TABS)[number]["key"];

export default function SubjectProgressView({
  category,
  subjects,
  attendedSlugs,
  reviewCounts,
}: {
  category: "theory" | "coding";
  subjects: SubjectAttendance[];
  attendedSlugs: Set<string>;
  /** slug → 회독 수 Map. 출석 그리드 칸·지도 노드에 "n회독"을 표기한다(JCG-2348). */
  reviewCounts: Map<string, number>;
}) {
  const [view, setView] = useState<ViewKey>("attendance");
  const firstSelectable =
    subjects.find((s) => categoryPathFor(category, s.subject))?.subject ??
    subjects[0]?.subject ??
    "";
  const [selected, setSelected] = useState<string>(firstSelectable);

  const selectedPath = categoryPathFor(category, selected);

  return (
    <div className="space-y-4">
      {/* 뷰 전환 토글 (출석 ↔ 지도) */}
      <div
        className="inline-flex rounded-lg border border-border bg-muted p-0.5"
        role="tablist"
        aria-label="진행상황 표시 방식"
      >
        {VIEW_TABS.map(({ key, label, icon: Icon }) => {
          const active = view === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setView(key)}
              className={cn(
                "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
                active
                  ? "bg-card text-amber-700 shadow-sm dark:text-amber-300"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
              {label}
            </button>
          );
        })}
      </div>

      {view === "attendance" ? (
        <AttendanceGrid
          category={category}
          subjects={subjects}
          attendedSlugs={attendedSlugs}
          reviewCounts={reviewCounts}
        />
      ) : (
        <div className="space-y-3">
          {/* 과목 선택 칩 */}
          <div className="flex flex-wrap gap-1.5">
            {subjects.map((s) => {
              const path = categoryPathFor(category, s.subject);
              const active = s.subject === selected;
              return (
                <button
                  key={s.subject}
                  type="button"
                  disabled={!path}
                  onClick={() => setSelected(s.subject)}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
                    active
                      ? "bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950"
                      : "bg-muted text-muted-foreground hover:text-foreground",
                    !path && "cursor-not-allowed opacity-40",
                  )}
                >
                  {s.subjectDisplayName}
                </button>
              );
            })}
          </div>

          {/* 선택한 과목 오솔길 — 가로 폭을 끝까지 쓰는 구불구불 뱀길(자연 높이) */}
          {selectedPath ? (
            <div className="w-full rounded-xl border border-border bg-card p-2">
              <SubjectProgressTrail
                categoryPath={selectedPath}
                attendedSlugs={attendedSlugs}
                reviewCounts={reviewCounts}
              />
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              이 과목의 학습 경로를 준비 중이에요.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** 출석 뷰 — 전 과목의 학습 경로 페이지를 컴팩트 체크 그리드로 쌓는다. */
function AttendanceGrid({
  category,
  subjects,
  attendedSlugs,
  reviewCounts,
}: {
  category: "theory" | "coding";
  subjects: SubjectAttendance[];
  attendedSlugs: Set<string>;
  /** slug → 회독 수 Map. 2회독 이상이면 칸에 숫자를 표기한다(JCG-2348). */
  reviewCounts: Map<string, number>;
}) {
  const router = useRouter();

  // 과목별 학습경로 + 출석 수를 한 번만 계산(렌더마다 buildTrailStations 재호출 방지).
  const rows = useMemo(
    () =>
      subjects.map((s) => {
        const path = categoryPathFor(category, s.subject);
        const stations = path ? buildTrailStations(path) : [];
        const doneCount = stations.filter((st) =>
          attendedSlugs.has(st.href.slice(1)),
        ).length;
        return {
          subject: s,
          path,
          stations,
          total: stations.length,
          doneCount,
        };
      }),
    [category, subjects, attendedSlugs],
  );

  const categoryDone = rows.reduce((sum, r) => sum + r.doneCount, 0);

  return (
    <div className="space-y-3">
      {/* 빈 상태 안내 — 이 카테고리에서 아직 출석한 페이지가 하나도 없을 때 */}
      {categoryDone === 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          아직 출석한 페이지가 없어요. 각 페이지의 출석체크 버튼을 누르면 출석으로
          인정돼요. 아래 칸을 누르면 해당 페이지로 바로 이동해요.
        </div>
      )}
      {rows.map(({ subject: s, path, stations, total, doneCount }) => {
        const pct = total > 0 ? (doneCount / total) * 100 : 0;

        return (
          <div
            key={s.subject}
            className="rounded-xl border border-border bg-card p-3"
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-sm font-bold text-foreground">
                {s.subjectDisplayName}
              </span>
              <span className="text-xs font-semibold tabular-nums text-muted-foreground">
                <span className="text-amber-600 dark:text-amber-400">
                  {doneCount}
                </span>{" "}
                / {total}
              </span>
            </div>

            <div className="mb-2.5 h-1.5 overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-amber-500 transition-[width] duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>

            {total > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {stations.map((st) => {
                  const slug = st.href.slice(1);
                  const isAttended = attendedSlugs.has(slug);
                  // 출석 칸의 회독 수 — Map 에 없으면 출석=1 폴백(미출석은 0). (JCG-2348)
                  const reviewCount =
                    reviewCounts.get(slug) ?? (isAttended ? 1 : 0);
                  // 2회독 이상은 Check 대신 회독 수를 굵게 표기해 "다회독" 페이지를 한눈에 구분.
                  const showCount = isAttended && reviewCount >= 2;
                  return (
                    <Tooltip key={st.href}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() =>
                            router.push(getTrailHref(st.href, path))
                          }
                          aria-label={`${st.title}${
                            isAttended ? ` (${reviewCount}회독)` : ""
                          }`}
                          className={cn(
                            "flex h-7 w-7 items-center justify-center rounded-md transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
                            isAttended
                              ? "bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950"
                              : "border border-border bg-muted text-muted-foreground hover:text-foreground",
                          )}
                        >
                          {showCount ? (
                            <span className="text-[11px] font-bold leading-none tabular-nums">
                              {reviewCount}
                            </span>
                          ) : isAttended ? (
                            <Check
                              className="h-4 w-4"
                              strokeWidth={3}
                              aria-hidden
                            />
                          ) : null}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-64 text-sm leading-snug"
                      >
                        {st.title}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                학습 경로를 준비 중이에요.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
